import type { UpdateCommentMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateCommentDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export interface UpdateCommentInput {
  Id: string;
  Content: string;
}

/**
 * Flag-gated wrapper (RSP-6496). Both paths return `{ Id }`.
 *
 * A non-author edit is denied server-side: the Apollo path rejects with a
 * `permission-error` GraphQL extension, the tRPC path with a `FORBIDDEN`
 * `TRPCClientError`. The wrapper surfaces the raw rejection unchanged — the
 * consumer (`useComments.tinycomments_edit_comment`) classifies EITHER shape
 * as `canEdit: false` via `isPermissionError` / `isTrpcPermissionError`.
 */
export const useUpdateComment = createMutationHook({
  name: 'updateComment',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.comment.update.mutationOptions({ onSuccess }),
  mapInput: (input: UpdateCommentInput) => input,
  invalidate: (trpc) => [
    trpc.frontend.comment.commentsByConversationId.queryKey(),
  ],
  mapResponse: (result: { Id: string }): { Id: string } => ({
    Id: result.Id,
  }),
  graphqlDocument: UpdateCommentDocument,
  graphqlVariables: (input: UpdateCommentInput) => ({
    Id: input.Id,
    Content: input.Content,
  }),
  mapGraphqlResponse: (data: UpdateCommentMutation): { Id: string } => {
    if (!data.update_comment_by_pk) {
      throw new Error('Failed to update comment');
    }

    return { Id: data.update_comment_by_pk.Id };
  },
});
